import classNames from 'classnames'
import { EmailInput, PhoneInput, TextInput } from 'components/common/field'
import AddressAutocomplete from 'components/common/field/AddressAutocomplete/AddressAutocompleteInput'
import ProfileImageSample from 'components/common/ProfileImageSample'
import { useTranslation } from 'react-i18next'

import { formatFullUserAddress, toAddressData } from './mySpaceUtils'

type MySpaceProfileCardProps = {
  userData: Record<string, unknown> | null
  isEditing: boolean
  isSaving: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (payload: any) => void
}

const MailIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)

const PhoneIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
)

const LocationIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const PencilIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
)

const MySpaceProfileCard = ({
  userData,
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSave,
  onChange,
}: MySpaceProfileCardProps) => {
  const { t } = useTranslation()
  const name = typeof userData?.name === 'string' ? userData.name : ''
  const email = typeof userData?.email === 'string' ? userData.email : ''
  const phone = typeof userData?.phone === 'string' ? userData.phone : ''
  const image = typeof userData?.image === 'string' ? userData.image : ''
  const location = formatFullUserAddress(userData?.address)

  return (
    <section
      id="my-space-profile"
      className={classNames(
        'scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6',
        isEditing ? 'overflow-visible' : 'overflow-hidden'
      )}
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 gap-4">
          <div className="relative shrink-0">
            <div className="h-20 w-20 overflow-hidden rounded-full border-2 border-white shadow-md sm:h-24 sm:w-24">
              {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
              ) : (
                <ProfileImageSample />
              )}
            </div>
            <span
              className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-imovPrimary text-white shadow"
              aria-hidden
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
          </div>
          <div className="min-w-0 pt-1">
            <h2 className="text-lg font-bold text-imovSecondary sm:text-xl">{name || '—'}</h2>
            {location ? (
              <p className="mt-1 flex min-w-0 items-start gap-1.5 text-sm leading-snug text-gray-500">
                <LocationIcon />
                <span className="min-w-0 flex-1 break-words line-clamp-2">{location}</span>
              </p>
            ) : null}
          </div>
        </div>

        {!isEditing ? (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 self-start rounded-xl bg-imovPrimary px-4 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark sm:w-auto"
          >
            <PencilIcon />
            {t('account.mySpace.editProfile')}
          </button>
        ) : (
          <div className="flex w-full shrink-0 gap-2 self-start sm:w-auto">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="rounded-xl bg-imovPrimary px-4 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <div className="mt-6 grid gap-4 border-t border-gray-100 pt-5 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('account.mySpace.emailLabel')}
            </p>
            <p className="mt-1.5 flex items-center gap-2 text-sm font-medium text-gray-800">
              <MailIcon />
              <span className="truncate">{email || '—'}</span>
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('account.mySpace.phoneLabel')}
            </p>
            <p className="mt-1.5 flex items-center gap-2 text-sm font-medium text-gray-800">
              <PhoneIcon />
              <span>{phone || '—'}</span>
            </p>
          </div>
        </div>
      ) : (
        <div className="relative z-20 mt-6 grid items-start gap-4 border-t border-gray-100 pt-5 sm:grid-cols-2 [&>*]:min-w-0">
          <TextInput
            value={name}
            name="name"
            label={t('auth.firstName')}
            onChange={onChange}
            customIcon="regular-user"
          />
          <AddressAutocomplete
            value={toAddressData(userData?.address)}
            name="address"
            label={t('auth.address')}
            onPlaceSelect={address => onChange({ name: 'address', value: address })}
            isFirstInput={false}
          />
          <EmailInput
            value={email}
            name="email"
            label={t('auth.emailAddress')}
            onChange={onChange}
            readOnly
          />
          <PhoneInput value={phone} name="phone" label={t('auth.phoneNumber')} onChange={onChange} />
        </div>
      )}
    </section>
  )
}

export default MySpaceProfileCard
